import React, { useContext, useRef, useMemo, useState } from 'react';
import { Context } from '@front10/helpers/dist/Context';
import { ThemeProvider } from '@front10/base-elements';
import { EmotionCache } from '../../containers/_commons/emotionCache';
/**
 * Atoms
 */
import { Listbox } from '../../lab/Listbox';
import { FilterButton } from '../../lab/FilterButton';
import { Panels } from '../../containers/VerticalBookingContainer/Panels';
import { FlexibleFilterContainer } from '../../lab/FlexibleFilterContainer';
/**
 * Hooks
 */
import { useIsMobileView } from '@front10/utils';

const FullyVaccinatedFilter = (props, ref) => {
  const {
    value,
    labelKey = 'cmp-location-selector-label-fully-vaccinated',
    options = [
      { label: 'cmp-fully-vaccinated-type-options-fv', value: 'FULLY_VACCINATED' },
      { label: 'cmp-fully-vaccinated-type-options-nfv', value: 'NOT_VACCINATED' }
    ],
    disabled,
    mobileView = false,
    onChange,
    filterButtonIconProps
  } = props;

  const {
    translate,
    direction,
    theme: { designTokens }
  } = useContext(Context);

  const title = translate('cmp-fully-vaccinated-title', 'Fully vaccinated');
  const valueTranslated = useMemo(() => {
    return options
      .filter((option) => option.value === value)
      .map((option) => {
        return translate(option.label);
      })
      .pop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translate, value]);

  const fullyVaccinatedOptions = useMemo(() => {
    return (Array.isArray(options) ? options : []).map((option) => ({
      ...option,
      label: translate(option.label)
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translate]);

  const fullyVaccinatedRef = useRef(null);
  const [fullyVaccinatedFilter, setFullyVaccinatedFilter] = useState(value);

  React.useEffect(() => {
    setFullyVaccinatedFilter(value);
  }, [value]);

  let isMobile = useIsMobileView();
  if (mobileView) {
    isMobile = true;
  }

  const listboxRef = useRef(null);

  React.useImperativeHandle(
    ref,
    () => ({
      focusToggleElement: () => {
        listboxRef.current.focusToggleElement();
      }
    }),
    []
  );

  const desktopLayout = (
    <Listbox
      filterButtonIconProps={filterButtonIconProps}
      ref={listboxRef}
      label={translate(labelKey, 'Select Fully vaccinated')}
      placeholder={title}
      options={fullyVaccinatedOptions}
      defaultValue={value}
      forcePlaceholder={disabled}
      focusWhenIsShow={false}
      disabled={disabled}
      onChange={(value) => {
        onChange({ value });
      }}
    />
  );

  const mobileLayout = (
    <FlexibleFilterContainer
      showSeparator
      saveOnClose={false}
      variant="layout.popover"
      label={title}
      title={title}
      disabledDoneButton={false}
      enabledDoneButton
      labelBottomBtn={translate('cmp-fully-vaccinated-done-button', 'Done')}
      toggleElement={
        <FilterButton
          iconSettings={filterButtonIconProps}
          value={value && !disabled ? `${valueTranslated} ` : title}
          aria-label={`${title} ${value ? valueTranslated : ''}`}
          active={Boolean(value)}
          disabled={disabled} // used to disabled the component
        />
      }
      onSave={() => {
        props.onChange({ value: fullyVaccinatedFilter });
      }}
      onDismiss={() => {
        setFullyVaccinatedFilter(value);
      }}
    >
      <Panels showSeparator={false}>
        {options.length >= 1 && (
          <Listbox
            ref={fullyVaccinatedRef}
            label={translate('cmp-fully-vaccinated-placeholder', 'Select Fully vaccinated')}
            layout="standard"
            options={fullyVaccinatedOptions}
            key={Math.floor(Math.random() * 100 + 1)} // ???
            defaultValue={fullyVaccinatedFilter}
            onChange={(newFlightType) => {
              setFullyVaccinatedFilter(newFlightType);
            }}
          />
        )}
      </Panels>
    </FlexibleFilterContainer>
  );

  return (
    <EmotionCache direction={direction}>
      <ThemeProvider theme={designTokens}>{!isMobile ? desktopLayout : mobileLayout}</ThemeProvider>
    </EmotionCache>
  );
};

export default React.forwardRef(FullyVaccinatedFilter);
